import { useSnackbar } from 'notistack';
import { useContext, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { Audience } from '../../api/CustomQuestion/types/Audience';
import venturesAPI from '../../api/ventures';
import { Venture } from '../../api/ventures/types/Venture';
import { SnackMessage } from '../../components/common';
import CustomForm from '../../components/forms/custom-form';
import VentureAdditionalInfoForm, {
  FormAdditionalInfo,
} from '../../components/forms/venture-additional-info';
import { UserContext } from '../../contexts/user-context';
import { VentureContext } from '../../contexts/venture-context';
import { Natural } from '../../utils/Num/Natural';
import { formatDateToRFC } from '../../utils/date';

interface VentureAdditionalInfoDefaultProps {
  hasAccessToUpdate?: boolean;
}

const getInitialValues = (venture?: Venture): FormAdditionalInfo => {
  const addInfo = venture?.venturesAdditionalInfo;
  const addInfoValues = addInfo
    ? {
        howDidYouFind: addInfo.howDidYouFind,
        isLegallyRegistered: addInfo.isLegallyRegistered,
        businessDescription: addInfo.businessDescription,
        currentProgress: addInfo?.currentProgress,
        requiredSupport: addInfo?.requiredSupport,
        marketSize: addInfo?.marketSize,
        reasonToStartNow: addInfo?.reasonToStartNow,
        plans: addInfo?.plans,
        growthRatio: addInfo?.growthRatio,
        dedication: addInfo?.dedication,
        yearPlans: addInfo?.yearPlans,
        obstacles: addInfo?.obstacles,
        mentorshipPlans: addInfo?.mentorshipPlans,
        comments: addInfo?.comments,
      }
    : {
        howDidYouFind: null,
        isLegallyRegistered: null,
        businessDescription: null,
        currentProgress: null,
        requiredSupport: null,
        marketSize: null,
        reasonToStartNow: null,
        plans: null,
        growthRatio: null,
        dedication: null,
        yearPlans: null,
        obstacles: null,
        mentorshipPlans: null,
        comments: null,
      };

  const ventureValues = venture
    ? {
        numOfEmployees: venture.numOfEmployees,
        isTech: venture.isTech,
      }
    : {
        numOfEmployees: 0 as Natural,
        isTech: false,
      };

  return {
    ...addInfoValues,
    ...ventureValues,
  };
};

const getParsedValues = (value: FormAdditionalInfo) => {
  return {
    howDidYouFind: value.howDidYouFind,
    isLegallyRegistered: value.isLegallyRegistered,
    businessDescription: value.businessDescription,
    currentProgress: value.currentProgress,
    requiredSupport: value.requiredSupport,
    marketSize: value.marketSize,
    reasonToStartNow: value.reasonToStartNow,
    plans: value.plans,
    growthRatio: value.growthRatio,
    dedication: value.dedication,
    yearPlans: value.yearPlans,
    obstacles: value.obstacles,
    mentorshipPlans: value.mentorshipPlans,
    comments: value.comments,
    numOfEmployees: value.numOfEmployees,
    isTech: value.isTech,
  };
};

function VentureAdditionalInfoPage() {
  const location = useLocation();
  const { ventureId } = useParams<{ ventureId: string }>();
  const { user, hasAccessToAction } = useContext(UserContext);

  const { isExternalPage, hasAccessToUpdate } = useMemo(() => {
    const isExternalPage = location.pathname.includes('external');
    const hasAccessToUpdate = isExternalPage
      ? true
      : hasAccessToAction('venture.additionalInfo.update');
    return {
      isExternalPage,
      hasAccessToUpdate,
    };
  }, [hasAccessToAction, location.pathname]);

  return (
    <div>
      {user?.customQuestionsVentureAdditionalInfoEnabled ? (
        <CustomForm
          audience={Audience.VENTURE_ADDITIONAL_INFO}
          responderId={ventureId}
          successMessage='Additional information successfully updated.'
          hasAccessToUpdate={hasAccessToUpdate}
          isExternalPage={isExternalPage}
        />
      ) : (
        <VentureAdditionalInfoDefault hasAccessToUpdate={hasAccessToUpdate} />
      )}
    </div>
  );
}

function VentureAdditionalInfoDefault({
  hasAccessToUpdate,
}: VentureAdditionalInfoDefaultProps) {
  const { ventureId } = useParams<{ ventureId: string }>();
  const { venture, updateVenture } = useContext(VentureContext);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const initialValues = getInitialValues(venture);

  const handleSubmitForm = async (value: FormAdditionalInfo) => {
    try {
      setIsLoading(true);
      if (venture) {
        const parsedValues = getParsedValues(value);
        const { numOfEmployees, isTech, ...additionalInfoValues } =
          parsedValues;
        const venturesAdditionalInfo =
          venture?.venturesAdditionalInfo &&
          venture?.venturesAdditionalInfo.ventureId
            ? {
                ...venture?.venturesAdditionalInfo,
                ...additionalInfoValues,
              }
            : {
                ...additionalInfoValues,
                creationDate: formatDateToRFC(new Date()),
              };

        const updatedVenture = await venturesAPI.update(ventureId, {
          ...venture,
          numOfEmployees,
          isTech,
          venturesAdditionalInfo: venturesAdditionalInfo,
        });
        updateVenture(updatedVenture);
      }
      enqueueSnackbar('Additional information successfully updated.', {
        variant: 'success',
      });
    } catch (e: any) {
      const messageError = e.response?.data?.message;

      enqueueSnackbar('An error occurred while saving. Please, try again.', {
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: messageError,
          }),
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <VentureAdditionalInfoForm
        initialValues={initialValues}
        isLoading={isLoading}
        onSubmit={handleSubmitForm}
        hasAccessToUpdate={hasAccessToUpdate}
      />
    </div>
  );
}

export default VentureAdditionalInfoPage;
